import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Tag, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PayModal({ booking, onClose }) {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fee, setFee] = useState(0);
  const [loyalty, setLoyalty] = useState(null);
  const base = booking.slot?.price || booking.room?.price_per_hour || 0;
  useEffect(() => {
    if (user?.role === "client") api.get("/loyalty/me").then((r) => { if (r.data.credits > 0) setLoyalty(r.data); }).catch(() => {});
  }, [user?.role]);
  const discounted = promo ? promo.amount : loyalty ? Math.round(base * (100 - loyalty.percent)) / 100 : base;
  useEffect(() => {
    if (user?.role === "coach") api.get("/subscriptions/me").then((r) => setFee(r.data.pro ? 0 : r.data.service_fee)).catch(() => {});
    else api.get("/subscriptions/fee").then((r) => setFee(r.data.service_fee)).catch(() => {});
  }, [user?.role]);

  const check = async () => {
    if (!code.trim()) return;
    try {
      const r = await api.get("/promos/check", { params: { code, booking_id: booking.id } });
      setPromo(r.data); toast.success(`Code ${r.data.code} appliqué : -${r.data.percent}%`);
    } catch (e) { setPromo(null); toast.error(e.response?.data?.detail || "Code invalide"); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin, promo_code: promo ? promo.code : undefined });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Paiement indisponible"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="pay-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-900">Régler la séance</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="pay-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-3 text-sm text-slate-600">{booking.room?.name} · {booking.discipline}</div>
        {fee > 0 && <div className="mt-2 text-xs text-slate-500 flex justify-between"><span>Frais de service TymCoach</span><span>{fee.toFixed(2)} € <Link to="/tableau-de-bord" className="text-amber-700 underline ml-1">0 € avec Coach Pro</Link></span></div>}
        {fee === 0 && user?.role === "coach" && <div className="mt-2 text-xs text-emerald-700 font-semibold">Coach Pro : 0 € de frais de service</div>}
        <div className="mt-4 flex gap-2">
          <div className="flex-1 flex items-center border border-slate-200 rounded-xl px-3">
            <Tag className="w-4 h-4 text-rose-500" />
            <input data-testid="pay-promo-input" className="flex-1 p-2 text-sm uppercase outline-none" placeholder="Code promo (optionnel)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && check()} />
          </div>
          <button data-testid="pay-promo-apply" onClick={check} className="sc-btn sc-btn-outline text-sm">Appliquer</button>
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div className="text-sm text-slate-500">Montant</div>
          <div className="text-right">
            {(promo || loyalty) && <div className="text-sm text-slate-400 line-through">{base.toFixed(2)} €</div>}
            <div data-testid="pay-amount" className="text-2xl font-extrabold text-slate-900">{(discounted + fee).toFixed(2)} €</div>
            {promo && <div className="text-xs text-emerald-700 font-semibold" data-testid="pay-discount">-{promo.percent}% avec {promo.code}</div>}
            {!promo && loyalty && <div className="text-xs text-rose-600 font-semibold" data-testid="pay-loyalty">-{loyalty.percent}% récompense fidélité (appliquée automatiquement)</div>}
          </div>
        </div>
        <button data-testid="pay-confirm" onClick={pay} disabled={busy} className="mt-4 w-full sc-btn sc-btn-emerald justify-center disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Payer avec Stripe
        </button>
      </div>
    </div>
  );
}
